package one.devsbox.www.ui.campaigns

import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.ImageAddOutline
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import one.devsbox.commons.EMPTY
import one.devsbox.shared.ads.CampaignState
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.io.Repository
import one.devsbox.www.ui.widgets.ScreenContentAction
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.www_portal.generated.resources.Res
import one.devsbox.www.www_portal.generated.resources.ui_campaigns_listing_btn_new
import tk.mallumo.compose.navigation.navTo_CampaignDUI
import tk.mallumo.compose.navigation.navTo_CampaignEUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM

import tk.mallumo.utils.DataState

@VM
class ListingCampaignsVM : NavigationViewModel() {
    private lateinit var ctx: ListingCampaignsCtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var state by mutableStateOf<DataState<SharedIO.Campaigns>>(DataState.Loading())
        private set

    var loadingMore by mutableStateOf(false)
        private set

    val search = TextFieldState()

    val defaultStates = listOf(CampaignState.ACTIVE, CampaignState.DRAFT, CampaignState.PAUSED)

    val selectedStates = mutableStateListOf(*defaultStates.toTypedArray())
    var selectedDate by mutableStateOf(LocalDate.EMPTY)

    interface Action {
        fun toggleState(campaignState: CampaignState)
        fun setDate(date: LocalDate)
        fun loadMore()
        fun reload()
        fun newCampaign()
        fun openDetail(id: Long)
        fun back()
    }

    val action = object : Action {
        override fun toggleState(campaignState: CampaignState) {
            if (selectedStates.contains(campaignState)) {
                selectedStates.remove(campaignState)
            } else {
                selectedStates.add(campaignState)
            }
            loadCampaigns()
        }

        override fun setDate(date: LocalDate) {
            selectedDate = date
            loadCampaigns()
        }

        override fun loadMore() {
            val currentState = state
            if (currentState is DataState.Result && !loadingMore && currentState.nnvl.items.size < currentState.nnvl.count) {
                loadCampaigns(page = currentState.nnvl.page + 1)
            }
        }

        override fun reload() {
            loadCampaigns()
        }

        override fun newCampaign() {
            ctx.nav.parentNavigation?.navTo_CampaignEUI()
        }

        override fun openDetail(id: Long) {
            ctx.nav.navTo_CampaignDUI(ArgsId(id))
        }

        override fun back() {
            ctx.nav.up()
        }
    }

    fun setup(ctx: ListingCampaignsCtx) {
        this.ctx = ctx
        loadCampaigns()
    }

    private fun loadCampaigns(page: Int = 0) {
        if (page == 0) state = DataState.Loading()
        else loadingMore = true

        val stateMask = if (selectedStates.isEmpty()) {
            CampaignState.entries.sumOf { it.mask }
        } else {
            selectedStates.sumOf { it.mask }
        }

        scope.launch {
            runCatching {
                Repository.ads.campaign.listing(stateMask, page, selectedDate)
            }.onSuccess { response ->
                if (page == 0) {
                    state = DataState.Result(response)
                } else {
                    val currentResources = (state as? DataState.Result)?.nnvl
                    if (currentResources != null) {
                        state = DataState.Result(
                            currentResources.copy(
                                page = response.page,
                                items = currentResources.items + response.items
                            )
                        )
                    }
                }
                loadingMore = false
            }.onFailure {
                if (page == 0) state = DataState.Error(it.message ?: "Unknown error")
                loadingMore = false
            }.onSuccess {
                setupActions(it)
            }
        }
    }

    override fun onRelease() {}

    private fun setupActions(result: SharedIO.Campaigns) {
        contentActions = buildList {
            if (ctx.me.permission.campaign.write && !ctx.me.info.adminMode) {
                add(
                    ScreenContentAction(
                        text = Res.string.ui_campaigns_listing_btn_new,
                        color = ctx.colorPrimaryContainer,
                        icon = Svg.ImageAddOutline
                    ) { action.newCampaign() })
            }
        }
    }

}



